import React, { useState, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { defer, debounce } from 'lodash/fp';

import { DeviceFormFactor } from '../../../../common/platform/platformTypes';

import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

import { getTemplateId, getTemplateIsCustom, matchTemplate } from '../boardTemplatePickerUtil';

import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';

import Icon from '../../../components/icons/Icon';
import SearchClearButton from '../../../components/form/SearchClearButton';

import CategoryButton from './BoardTemplatePickerBodyListingCategoryButton';
import TemplateButton from './BoardTemplatePickerBodyListingTemplateButton';

import './BoardTemplatePickerBodyCategoriesList.scss';

const TemplateSearchResults = ({ searchResults, selectedTemplateId, selectTemplate, templates }) => {
    if (searchResults.size === 0) {
        return (
            <div className="picker-templates">
                <div className="no-results">No templates found</div>
            </div>
        );
    }

    return (
        <ul className="picker-templates">
            {searchResults.map((template) => (
                <li key={getTemplateId(template)}>
                    <TemplateButton
                        templateEntry={template}
                        templates={templates}
                        isCustomTemplate={getTemplateIsCustom(template)}
                        selectedTemplateId={selectedTemplateId}
                        selectTemplate={selectTemplate}
                    />
                </li>
            ))}
        </ul>
    );
};

TemplateSearchResults.propTypes = {
    searchResults: PropTypes.object,
    templates: PropTypes.object,
    selectedTemplateId: PropTypes.string,
    selectTemplate: PropTypes.func,
};

const trackSearchAnalytics = debounce(2000, (searchTerm) =>
    sendAmplitudeEvent({
        eventType: 'Searched templates',
        eventProperties: {
            searchTerm,
        },
    }),
);

const BoardTemplatePickerCategoriesList = ({
    scrollPosition,
    selectedTemplateId,
    templateCollections,
    templates,
    setScrollPosition,
    setNavigation,
    setHeight,
    selectTemplate,
}) => {
    const [search, setSearch] = useState('');
    const searchInputRef = useRef();
    const pickerBodyRef = useRef();
    const platformDetails = useSelector(getPlatformDetailsSelector);

    const focusSearchInput = () => searchInputRef.current && searchInputRef.current.focus();

    const handleSearchSubmit = (event) => event.preventDefault();

    const handleSearchInputChange = (event) => {
        const searchTerm = event.target.value;
        setSearch(searchTerm);
        trackSearchAnalytics(searchTerm);
    };

    const handleSearchInputClear = () => {
        setSearch('');
        defer(focusSearchInput);
    };

    const shouldFocusSearchInput = (platformDetails) => platformDetails.formFactor !== DeviceFormFactor.tablet;

    useLayoutEffect(() => {
        // wait until transition is complete before focusing search input,
        // otherwise, safari tries to scroll to the input and it breaks the transition
        if (shouldFocusSearchInput(platformDetails)) {
            setTimeout(() => {
                focusSearchInput();
                pickerBodyRef.current.scrollTop = scrollPosition;
            }, 400);
        }

        // on mount, scroll to the previous scroll position
        if (pickerBodyRef.current) pickerBodyRef.current.scrollTop = scrollPosition;

        // on unmount, save the scroll position of the category list
        return () => setScrollPosition(pickerBodyRef.current.scrollTop);
    }, []);

    const hasSearch = search.length > 0;
    const searchResults = templates.filter(matchTemplate(search)).toList();
    const displayedList = hasSearch ? searchResults : templateCollections;

    return (
        <div
            ref={pickerBodyRef}
            className={classNames('picker-body BoardTemplatePickerBodyCategoriesList', {
                scrollable: displayedList.size > 6,
            })}
        >
            <form className="search-form" onSubmit={handleSearchSubmit}>
                <input
                    ref={searchInputRef}
                    className="FormInput"
                    placeholder="Search templates..."
                    value={search}
                    onChange={handleSearchInputChange}
                />
                <SearchClearButton
                    show
                    canClear={!!search.length}
                    onMouseDown={handleSearchInputClear}
                    onClick={handleSearchInputClear}
                />
            </form>

            {hasSearch ? (
                <TemplateSearchResults
                    selectedTemplateId={selectedTemplateId}
                    selectTemplate={selectTemplate}
                    searchResults={searchResults}
                    templates={templates}
                />
            ) : (
                <ul className="picker-categories">
                    {templateCollections.map((category) => (
                        <li key={category.get('id')}>
                            <CategoryButton categoryId={category.get('id')} setNavigation={setNavigation}>
                                {category.get('title')}

                                <span className="count">{category.get('templates').size}</span>

                                <Icon name="chevron-left" className="nav-right" />
                            </CategoryButton>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

BoardTemplatePickerCategoriesList.propTypes = {
    scrollPosition: PropTypes.number,
    selectedTemplateId: PropTypes.string,
    navigation: PropTypes.string,
    templates: PropTypes.object,
    templateCollections: PropTypes.object,
    setScrollPosition: PropTypes.func,
    selectTemplate: PropTypes.func,
    handleCloseButton: PropTypes.func,
    setNavigation: PropTypes.func,
    setHeight: PropTypes.func,
};

export default BoardTemplatePickerCategoriesList;
